import { Image } from "@heroui/react";
import { FocusScope } from "@react-aria/focus";
import clsx from "clsx";
import { APP_CLINIC, DOCUMENT_PRIVACY_POLICY } from "core/consts";
import {
  ENV_DEMO,
  ENV_PREPROD,
  ENV_PRODUCTION,
  ENV_STAGING,
  getStaticAsset,
  isAcpApp,
  isDemoEnv,
  isPreprodEnv,
  isProdEnv,
  isReceiverApp,
  isSenderApp,
} from "core/model/config";
import {
  activateEnvSwitch,
  isDeveloperMode,
} from "core/model/utils/featureFlags";
import { capitaliseFirst } from "core/model/utils/strings";
import { Env } from "core/types";
import { useExternalLinkLabel } from "ds_legacy/components/LinkV2";
import { getRecareLogoWithNamePath } from "ds_legacy/components/RecareLogo";
import {
  Banner,
  Button,
  ControlledInput,
  ControlledPasswordInput,
  Form,
  Link,
  Spinner,
} from "ds/ui";
import { useLegalDocuments } from "dsl/atoms/LegalDocuments";
import { setCurrentAppOptions } from "dsl/ecosystems/CareproviderOnboardingLoginPage/LoginSuggestions";
import {
  LoginErrorState,
  LoginView,
} from "dsl/ecosystems/CareproviderOnboardingLoginPage/useHandleLogin";
import { useFocusElement } from "dsl/hooks/useFocusElement";
import { ArrowLeftIcon, LockIcon } from "lucide-react";
import { FormProvider } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useTranslations } from "translations";
import { loginPage, LoginPageVariants } from "./classes";
import { LoginParsedValues } from "./loginSchema";
import { useLoginForm } from "./useLoginForm";

type LoginPageApp = NonNullable<LoginPageVariants["app"]>;

export type LoginPageProps = LoginPageVariants & {
  /**
   * Autofocus strategy. `"container"` focuses the first focusable element on
   * mount. `"back-button"` focuses the challenge-view back button via
   * `FocusScope`. Omit for no autofocus.
   */
  autoFocus?: "back-button" | "container";
  challengeTimestamp: string | undefined;
  contactUsUrl?: string;
  env: Env;
  goToForgotPassword: string;
  goToProductionUrl: string;
  isEmailInputLoading?: boolean;
  isLoading: boolean;
  isSingleSignOn?: boolean;
  loginErrors?: LoginErrorState;
  loginView: LoginView;
  onBackToLogin: () => void;
  onChangeEmail?: (value: string) => void;
  onSubmit: (payload: LoginParsedValues) => void;
  otherAppUrl?: string;
  resetError: () => void;
  ssoToken?: string;
};

const ENVIRONMENTS = [
  ENV_STAGING,
  ENV_PREPROD,
  ENV_DEMO,
  ENV_PRODUCTION,
] as const;

type LoginSubmitButtonId = "login" | "login_challenge" | "login_sso";

function getLoginSubmitButtonId({
  isLogin,
  isSingleSignOn,
}: {
  isLogin: boolean;
  isSingleSignOn: boolean | undefined;
}): LoginSubmitButtonId {
  if (!isLogin) {
    return "login_challenge";
  }
  if (isSingleSignOn) {
    return "login_sso";
  }
  return "login";
}

function getLoginPageTitle({
  app,
  isLogin,
  translations,
}: {
  app: LoginPageApp;
  isLogin: boolean;
  translations: ReturnType<typeof useTranslations>;
}): string {
  if (!isLogin) {
    return translations.login.loginPage.titleAccountVerification;
  }
  if (isSenderApp(app)) {
    return translations.login.loginPage.titleSender;
  }
  if (isReceiverApp(app)) {
    return translations.login.loginPage.titleReceiver;
  }
  return translations.login.loginPage.titleAcp;
}

function LoginPageDevTools({
  app,
  env,
  isLoading,
  navigate,
}: {
  app: LoginPageApp;
  env: Env;
  isLoading: boolean;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const classes = loginPage({ app: app, env });
  const appOptions = setCurrentAppOptions({ currentApp: app, env });

  if (!isDeveloperMode) {
    return null;
  }

  return (
    <div className={classes.devToolContainer()}>
      {activateEnvSwitch && (
        <div
          className={clsx(
            classes.envSwitcher(),
            "rounded-md border border-primary bg-white p-2",
          )}
        >
          {ENVIRONMENTS.map((envOption) => (
            <Button
              key={envOption}
              color="primary"
              id={`env_switch_${envOption}`}
              isDisabled={isLoading}
              size="sm"
              variant={env === envOption ? "solid" : "light"}
              onPress={() => navigate({ search: `?env=${envOption}` })}
            >
              {capitaliseFirst(envOption)}
            </Button>
          ))}
        </div>
      )}

      {activateEnvSwitch && (
        <div className={classes.appSuggestion()}>
          {appOptions.map(({ href, label }) => (
            <Link
              key={label}
              color="primary-dark"
              href={href}
              id={label}
              size="sm"
            >
              {label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function LoginChallengeBackButton({
  autoFocus,
  label,
  onPress,
}: {
  autoFocus: LoginPageProps["autoFocus"];
  label: string;
  onPress: () => void;
}) {
  const button = (
    <Button
      className="self-start pl-0"
      color="primary"
      id="back_to_login"
      size="sm"
      startContent={<ArrowLeftIcon />}
      variant="light"
      onPress={onPress}
    >
      {label}
    </Button>
  );

  return (
    <>
      {autoFocus === "back-button" ? (
        <FocusScope autoFocus>{button}</FocusScope>
      ) : (
        button
      )}
    </>
  );
}

function LoginPageTitlePrefix({
  app,
  env,
  translations,
}: {
  app: LoginPageApp;
  env: Env;
  translations: ReturnType<typeof useTranslations>;
}) {
  const isDemo = isDemoEnv(env);
  const isProd = isProdEnv(env);
  const isPreprod = isPreprodEnv(env);

  return (
    <>
      {(isPreprod || isProd) && isAcpApp(app) && (
        <span className="font-normal">
          {isProd
            ? translations.acp.login.prefixProd
            : translations.acp.login.prefixPreprod}{" "}
          |{" "}
        </span>
      )}
      {isDemo && (
        <span className="font-normal">
          {translations.login.loginPage.titleDemo} |{" "}
        </span>
      )}
    </>
  );
}

export function LoginPage({
  app = APP_CLINIC,
  autoFocus,
  challengeTimestamp,
  contactUsUrl,
  env,
  goToForgotPassword,
  goToProductionUrl,
  isEmailInputLoading,
  isLoading,
  isSingleSignOn,
  loginErrors,
  loginView = "login",
  onBackToLogin,
  onChangeEmail,
  onSubmit,
  otherAppUrl,
  resetError,
  ssoToken,
}: LoginPageProps) {
  const translations = useTranslations();
  const { getDocumentUrl } = useLegalDocuments();
  const externalLinkLabel = useExternalLinkLabel();
  const navigate = useNavigate();
  const classes = loginPage({ app, env });

  const { handleBackToLogin, handleSubmit, methods } = useLoginForm({
    isSingleSignOn,
    loginErrors,
    loginView,
    onBackToLogin,
    onChangeEmail,
    onSubmit,
    resetError,
    ssoToken,
  });

  const isDemo = isDemoEnv(env);
  const isLogin = loginView === "login";
  const isChallenge = loginView === "challenge";
  const showPasswordField = isLogin && !isSingleSignOn;
  const submitButtonId = getLoginSubmitButtonId({
    isLogin,
    isSingleSignOn,
  });

  const focusRef = useFocusElement<HTMLDivElement>({
    focusFirstInnerElement: true,
    shouldFocus: autoFocus === "container",
  });

  return (
    <FormProvider {...methods}>
      <div className={classes.page()}>
        <LoginPageDevTools
          app={app}
          env={env}
          isLoading={isLoading}
          navigate={navigate}
        />
        <div ref={focusRef} className={classes.container()}>
          <div className={classes.content()}>
            <div className={classes.header()}>
              <Image
                alt="Recare logo"
                src={getStaticAsset(getRecareLogoWithNamePath("master"))}
                width={160}
              />
            </div>
            <Form className={classes.form()} onSubmit={handleSubmit}>
              <>
                <div className={clsx(classes.formContent(), "w-full")}>
                  {isChallenge && (
                    <LoginChallengeBackButton
                      autoFocus={autoFocus}
                      label={translations.actions.back}
                      onPress={handleBackToLogin}
                    />
                  )}
                  <h1 className={classes.title()}>
                    <LoginPageTitlePrefix
                      app={app}
                      env={env}
                      translations={translations}
                    />
                    {getLoginPageTitle({ app, isLogin, translations })}
                  </h1>
                  {isChallenge && (
                    <p className="text-sm">
                      {translations.login.loginPage.accountVerificationInfo}
                    </p>
                  )}
                  {isDemo && (
                    <Banner
                      color="warning"
                      message={
                        <p>
                          {translations.login.loginPage.environmentInfo}{" "}
                          <Link
                            aria-label={externalLinkLabel(
                              translations.login.loginPage.productionLink,
                              "website",
                            )}
                            color="primary-dark"
                            href={goToProductionUrl}
                            id="go-to-prod"
                            size="sm"
                          >
                            {translations.login.loginPage.productionLink}
                          </Link>
                          .
                        </p>
                      }
                      size="sm"
                    />
                  )}
                </div>
                <div className={clsx(classes.formContent(), "w-full")}>
                  {loginErrors?.general && (
                    <Banner
                      color="danger"
                      data-testid="login-error"
                      message={loginErrors.general}
                      role="alert"
                    />
                  )}
                  {isChallenge && challengeTimestamp && (
                    <p className="text-sm">
                      {translations.login.challengeScreen.infoTextTimestamp({
                        datetimestring: challengeTimestamp,
                      })}
                    </p>
                  )}
                  <ControlledInput
                    autoComplete="email"
                    endContent={
                      isEmailInputLoading ? (
                        <Spinner size="sm" testId="email-input-spinner" />
                      ) : undefined
                    }
                    label={translations.login.loginPage.email}
                    name="email"
                    placeholder={translations.login.loginPage.emailPlaceholder}
                    required
                    type={isLogin ? "email" : "hidden"}
                  />
                  {isSingleSignOn && (
                    <div
                      aria-live="polite"
                      className="flex max-w-fit items-center space-x-2 rounded-md border border-success bg-success-light py-1.5 pr-5 pl-3 text-success-dark"
                      role="alert"
                    >
                      <LockIcon aria-hidden="true" size={14} />{" "}
                      <p className="text-sm">
                        {translations.login.loginPage.ssoInfo}
                      </p>
                    </div>
                  )}
                  <ControlledPasswordInput
                    autoComplete={
                      showPasswordField ? "current-password" : "off"
                    }
                    label={translations.login.loginPage.password}
                    name="password"
                    placeholder={
                      translations.login.loginPage.passwordPlaceholder
                    }
                    required
                    type={showPasswordField ? "password" : "hidden"}
                  />
                  <ControlledInput
                    autoComplete="off"
                    label={translations.login.loginPage.verificationCode}
                    name="challenge"
                    placeholder={
                      translations.login.loginPage.verificationCodePlaceholder
                    }
                    required
                    type={isLogin ? "hidden" : "text"}
                  />
                  {isLogin && !isSingleSignOn && (
                    <p>
                      <Link
                        color="primary-dark"
                        href={goToForgotPassword}
                        id="forgot-password"
                        size="sm"
                      >
                        {translations.login.loginPage.forgotPassword}
                      </Link>
                    </p>
                  )}
                </div>
                <div className="flex w-full justify-between">
                  <Button
                    color="primary"
                    fullWidth
                    id={submitButtonId}
                    isLoading={isLoading}
                    size="lg"
                    type="submit"
                    variant="solid"
                  >
                    {translations.login.loginPage.loginButton}
                  </Button>
                </div>
              </>
            </Form>
          </div>
          <div className={classes.footer()}>
            {isLogin && !isAcpApp(app) && (
              <div className={classes.footerLinks()}>
                <p>
                  {translations.login.loginPage.contactInfo}{" "}
                  <Link
                    aria-label={externalLinkLabel(
                      translations.login.loginPage.contactInfoLink,
                      "website",
                    )}
                    color="primary-dark"
                    href={contactUsUrl}
                    id="contact-us"
                    isExternal
                    size="sm"
                  >
                    {translations.login.loginPage.contactInfoLink}
                  </Link>
                </p>
                <p>
                  {isSenderApp(app)
                    ? translations.login.loginPage.receiverInfo
                    : translations.login.loginPage.clinicInfo}{" "}
                  <Link
                    aria-label={externalLinkLabel(
                      translations.login.clinic.clickHere,
                      "website",
                    )}
                    color="primary-dark"
                    href={otherAppUrl}
                    id="switch-app"
                    size="sm"
                  >
                    {isSenderApp(app)
                      ? translations.login.loginPage.receiverInfoLink
                      : translations.login.loginPage.clinicLink}
                  </Link>
                </p>
              </div>
            )}
            <hr className={classes.footerDivider()} />
            <p>
              {translations.login.loginPage.privacyPolicyInfo}{" "}
              <Link
                aria-label={externalLinkLabel(
                  translations.login.privacyPolicy,
                  "pdf",
                )}
                color="primary-dark"
                href={getDocumentUrl(DOCUMENT_PRIVACY_POLICY)}
                id="privacy-policy"
                isExternal
                size="sm"
              >
                {translations.login.privacyPolicy}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </FormProvider>
  );
}
